import { useMemo, useState } from "react";
import { useStore } from "../lib/store";
import { salvaClassificacoes } from "../features/classifications/save";
import { showFeedback } from "../lib/feedback";
import type { FinanceContract, FinanceInvoice } from "../features/finance/contracts";
import type { Meeting } from "../features/meetings/contracts";
import { invoiceBalanceCents } from "../features/finance/contracts";
import { formatCents, parseCents } from "../features/finance/format";
import { civilDate } from "../features/notifications/feed";
import { localParts, toInstant } from "../features/tasks/civil-time";
import type { Lead, LeadFieldValue } from "../lib/types";
import {
  LEAD_FORM_STEPS,
  leadFormFromLead,
  leadFormToDraft,
  stepOfError,
  validateLeadForm,
  formFieldsOf,
  type LeadFormStepId,
  type LeadFormValues,
} from "../features/leads/form";
import { LeadFormStep } from "../features/leads/LeadFormFields";
import { LeadPhotoField } from "../features/leads/LeadPhotoField";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { Field, TextArea, TextInput } from "../ui/Field";
import { Select } from "../ui/Select";
import { ToolbarSpacer } from "../ui/PageHead";

/**
 * Diálogos do perfil do lead.
 *
 * Editar usa a mesma definição de campos da criação, com a mesma validação e as
 * mesmas mensagens: é isso que a matriz do lead exige, e é por isso que este
 * arquivo não tem um formulário próprio (AGENTS §19.1).
 *
 * Os comandos financeiros e de reunião passam pelo domínio real; a tela só
 * traduz o que a pessoa digitou e mostra o erro que o comando devolveu (§25.1).
 */

/* --- Editar dados ------------------------------------------------------------ */

export function EditLeadDialog({
  lead,
  funnelId,
  onClose,
}: {
  lead: Lead;
  funnelId: string;
  onClose: () => void;
}) {
  const store = useStore();
  const fields = useMemo(() => formFieldsOf(store.leadFields, funnelId), [funnelId, store.leadFields]);
  const initial = useMemo(() => {
    const values: Record<string, LeadFieldValue["value"]> = {};
    for (const value of store.leadFieldValues)
      if (value.leadId === lead.id) values[value.fieldId] = value.value;
    const escolhas: Record<string, string> = {};
    for (const value of store.leadClassificationValues ?? [])
      if (value.leadId === lead.id) escolhas[value.definitionId] = value.optionId;
    return leadFormFromLead(lead, values, escolhas);
  }, [lead, store.leadFieldValues, store.leadClassificationValues]);

  const [values, setValues] = useState<LeadFormValues>(initial);
  const [step, setStep] = useState<LeadFormStepId>("identificacao");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [focusKey, setFocusKey] = useState<string>();
  const [saving, setSaving] = useState(false);
  /** Foto escolhida e ainda não enviada: só vira dado ao salvar. */
  const [photo, setPhoto] = useState<File | null>(null);

  const dirty = JSON.stringify(values) !== JSON.stringify(initial) || photo !== null;
  const position = LEAD_FORM_STEPS.findIndex((item) => item.id === step);

  const submit = async () => {
    if (saving) return;
    const found = validateLeadForm(values, fields);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      setStep(stepOfError(first, fields));
      setFocusKey(first);
      return;
    }
    setSaving(true);
    const draft = leadFormToDraft(values);
    const saved = await store.updateLead(lead.id, draft.lead);
    if (saved) {
      await store.saveLeadFieldValues(lead.id, draft.fieldValues);
      await salvaClassificacoes(store, lead.id, initial.classifications, values.classifications);
      // A foto é efeito fora da transação: falhar nela não desfaz o resto,
      // mas precisa aparecer em vez de sumir em silêncio (§8).
      if (photo && !(await store.setLeadPhoto(lead.id, photo))) {
        setSaving(false);
        showFeedback("Os dados foram salvos, mas a foto não subiu. Tente enviá-la de novo.");
        setPhoto(null);
        return;
      }
    }
    setSaving(false);
    if (!saved) return;
    showFeedback("Dados do lead atualizados.", "success");
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={dirty}
      width="wide"
      title="Editar dados"
      description="Mesma definição de campos do cadastro: o que muda aqui muda em todas as superfícies."
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button
            disabled={position === 0 || saving}
            onClick={() => setStep(LEAD_FORM_STEPS[Math.max(0, position - 1)].id)}
          >
            Voltar
          </Button>
          {position < LEAD_FORM_STEPS.length - 1 ? (
            <Button
              variant="primary"
              onClick={() => setStep(LEAD_FORM_STEPS[position + 1].id)}
            >
              Continuar
            </Button>
          ) : (
            <Button variant="primary" onClick={submit} loading={saving}>
              Salvar alterações
            </Button>
          )}
        </>
      }
    >
      <div className="cx-steps" role="list" aria-label="Etapas do cadastro">
        {LEAD_FORM_STEPS.map((item, index) => (
          <button
            key={item.id}
            type="button"
            role="listitem"
            className="cx-step"
            data-active={item.id === step || undefined}
            data-done={index < position || undefined}
            onClick={() => setStep(item.id)}
          >
            <span className="cx-step-index">{index + 1}</span>
            <strong>{item.label}</strong>
            <small>{item.hint}</small>
          </button>
        ))}
      </div>

      {step === "identificacao" && (
        <LeadPhotoField currentUrl={lead.photoUrl} file={photo} onChange={setPhoto} />
      )}

      <LeadFormStep
        step={step}
        funnelId={funnelId}
        values={values}
        errors={errors}
        focusKey={focusKey}
        onChange={(patch) => setValues((current) => ({ ...current, ...patch }))}
      />
    </Dialog>
  );
}

/* --- Desfecho ----------------------------------------------------------------- */

export function LostDialog({
  membershipId,
  funnelId,
  onClose,
}: {
  membershipId: string;
  funnelId: string;
  onClose: () => void;
}) {
  const store = useStore();
  const reasons = store.lossReasons.filter(
    (reason) => reason.funnelId === funnelId && !reason.archived,
  );
  const [reasonId, setReasonId] = useState<string | null>(reasons[0]?.id ?? null);
  const [error, setError] = useState("");

  const submit = () => {
    if (!reasonId) {
      setError("Escolha o motivo da perda.");
      return;
    }
    store.setMembershipOutcome(membershipId, "lost", reasonId);
    showFeedback("Negociação marcada como perdida.", "success");
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={false}
      title="Marcar como perdida"
      description="O motivo fica registrado na jornada e alimenta a análise de perdas."
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="danger" onClick={submit} disabled={reasons.length === 0}>
            Marcar perdida
          </Button>
        </>
      }
    >
      {reasons.length ? (
        <Field label="Motivo da perda" required error={error || undefined}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              describedBy={describedBy}
              size="field"
              label="Motivo da perda"
              value={reasonId}
              options={reasons.map((reason) => ({ value: reason.id, label: reason.name }))}
              onChange={(value) => {
                setReasonId(value);
                setError("");
              }}
            />
          )}
        </Field>
      ) : (
        <p className="cx-note">
          Este funil ainda não tem motivos de perda cadastrados. Cadastre um em Configurações antes
          de encerrar a negociação: perda sem motivo não é registrada.
        </p>
      )}
    </Dialog>
  );
}

/* --- Nota --------------------------------------------------------------------- */

export function NoteDialog({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const store = useStore();
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    if (!text.trim()) {
      setError("Escreva a nota.");
      return;
    }
    store.addNote(leadId, text.trim());
    showFeedback("Nota registrada na jornada.", "success");
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={Boolean(text.trim())}
      title="Nova nota"
      description="A nota entra na jornada como registro interno e não conta como interação com o lead."
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="primary" onClick={submit}>
            Registrar nota
          </Button>
        </>
      }
    >
      <Field label="Nota" required error={error || undefined}>
        {({ id, describedBy, invalid }) => (
          <TextArea
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            rows={4}
            value={text}
            maxLength={2000}
            placeholder="Contexto para a próxima conversa"
            onChange={(event) => {
              setError("");
              setText(event.target.value);
            }}
          />
        )}
      </Field>
    </Dialog>
  );
}

/* --- Contato ------------------------------------------------------------------ */

/**
 * Pessoa da empresa que participa da decisão.
 *
 * O contato pertence permanentemente a uma Company (§27.2), então o cadastro só
 * existe quando o lead já tem empresa vinculada; o botão da ficha fica
 * desabilitado com o motivo quando não tem. Vincular ao lead é uma lista de
 * participantes: o primário continua sendo o que já era.
 */
export function ContactDialog({
  lead,
  contactId,
  onClose,
}: {
  lead: Lead;
  contactId?: string;
  onClose: () => void;
}) {
  const store = useStore();
  const existing = contactId
    ? store.contacts.find((item) => item.id === contactId)
    : undefined;
  const [name, setName] = useState(existing?.name ?? "");
  const [role, setRole] = useState(existing?.role ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = async () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Informe o nome da pessoa.";
    setErrors(next);
    if (Object.keys(next).length) return;
    try {
      if (existing) {
        await store.updateContact(existing.id, {
          name: name.trim(),
          role: role.trim() || undefined,
        });
        showFeedback("Contato atualizado.", "success");
      } else {
        const id = await store.addContact({
          companyId: lead.companyId ?? "",
          name: name.trim(),
          role: role.trim() || undefined,
        });
        if (id) {
          const ids = new Set(lead.contactIds ?? []);
          ids.add(id);
          // `setLeadContacts`, não `updateLead`: com participantes, exatamente
          // um é primário (§27.10). O primeiro vinculado assume o papel; os
          // seguintes entram sem mexer em quem já era.
          await store.setLeadContacts(lead.id, [...ids], lead.primaryContactId ?? id);
        }
        showFeedback("Contato vinculado ao cadastro.", "success");
      }
      onClose();
    } catch (error) {
      setErrors({ name: error instanceof Error ? error.message : "Não foi possível salvar." });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={name.trim() !== (existing?.name ?? "") || role.trim() !== (existing?.role ?? "")}
      title={existing ? "Editar contato" : "Adicionar contato"}
      description="Pessoas que participam da decisão desta empresa."
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button
            variant="primary"
            onClick={() => {
              void submit();
            }}
          >
            {existing ? "Salvar contato" : "Adicionar"}
          </Button>
        </>
      }
    >
      <div className="cx-form-grid">
        <Field label="Nome" required error={errors.name} className="cx-span-2">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={name}
              placeholder="Ex.: Mariana Lopes"
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <Field label="Função" className="cx-span-2">
          {({ id }) => (
            <TextInput
              id={id}
              value={role}
              placeholder="Ex.: Sócia-diretora"
              onChange={(event) => setRole(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/* --- Link --------------------------------------------------------------------- */

export function LinkDialog({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const store = useStore();
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = () => {
    const next: Record<string, string> = {};
    if (!label.trim()) next.label = "Dê um nome ao material.";
    // Só http e https: outro protocolo não é link de material (§26.3).
    if (!/^https?:\/\/\S+$/i.test(url.trim())) next.url = "Informe um endereço começando com https://";
    setErrors(next);
    if (Object.keys(next).length) return;
    store.addLink(leadId, label.trim(), url.trim());
    showFeedback("Material vinculado.", "success");
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={Boolean(label.trim() || url.trim())}
      title="Vincular material"
      description="Proposta, contrato ou qualquer material de apoio hospedado fora do CRM."
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="primary" onClick={submit}>
            Vincular
          </Button>
        </>
      }
    >
      <div className="cx-form-grid">
        <Field label="Nome" required error={errors.label} className="cx-span-2">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={label}
              placeholder="Ex.: proposta comercial"
              onChange={(event) => setLabel(event.target.value)}
            />
          )}
        </Field>
        <Field label="Endereço" required error={errors.url} className="cx-span-2">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={url}
              placeholder="https://"
              onChange={(event) => setUrl(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/* --- Contrato ------------------------------------------------------------------ */

export function ContractDialog({
  leadId,
  membershipId,
  onClose,
}: {
  leadId: string;
  membershipId?: string;
  onClose: () => void;
}) {
  const store = useStore();
  const today = civilDate(new Date().toISOString());
  const [kind, setKind] = useState<"monthly" | "oneoff">("monthly");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [startOn, setStartOn] = useState(today);
  const [firstDueOn, setFirstDueOn] = useState(today);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (saving) return;
    const cents = parseCents(amount);
    const next: Record<string, string> = {};
    if (!description.trim()) next.description = "Descreva o contrato.";
    if (!cents) next.amount = "Informe um valor maior que zero.";
    if (firstDueOn < startOn) next.firstDueOn = "O primeiro vencimento não pode ser antes do início.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    const result = await store.createContract({
      leadId,
      membershipId,
      kind,
      description: description.trim(),
      amountCents: cents as number,
      startOn,
      firstDueOn,
    });
    setSaving(false);
    if (!result.ok) {
      setErrors({ description: result.error.message });
      return;
    }
    showFeedback(
      kind === "monthly"
        ? "Contrato mensal criado com as parcelas do horizonte."
        : "Contrato avulso criado com a fatura única.",
      "success",
    );
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={Boolean(description.trim() || amount.trim())}
      title="Novo contrato"
      description="O contrato não fecha a venda: quem fecha é o primeiro recebimento registrado."
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="primary" onClick={submit} loading={saving}>
            Criar contrato
          </Button>
        </>
      }
    >
      <div className="cx-form-grid">
        <Field label="Tipo" required>
          {({ id }) => (
            <Select
              id={id}
              size="field"
              label="Tipo do contrato"
              value={kind}
              options={[
                { value: "monthly", label: "Mensal", hint: "Gera parcelas recorrentes" },
                { value: "oneoff", label: "Avulso", hint: "Fatura única" },
              ]}
              onChange={(value) => setKind(value as "monthly" | "oneoff")}
            />
          )}
        </Field>
        <Field label="Valor" required error={errors.amount} help="Por parcela, no caso do mensal.">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={amount}
              placeholder="R$ 0,00"
              onChange={(event) => setAmount(event.target.value)}
            />
          )}
        </Field>
        <Field label="Descrição" required error={errors.description} className="cx-span-2">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={description}
              maxLength={200}
              placeholder="Ex.: gestão de tráfego mensal"
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>
        <Field label="Início" required>
          {({ id }) => (
            <TextInput
              id={id}
              type="date"
              value={startOn}
              onChange={(event) => setStartOn(event.target.value)}
            />
          )}
        </Field>
        <Field label="Primeiro vencimento" required error={errors.firstDueOn}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="date"
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={firstDueOn}
              onChange={(event) => setFirstDueOn(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/* --- Recebimento ---------------------------------------------------------------- */

export function ReceiptDialog({
  invoice,
  contract,
  onClose,
}: {
  invoice: FinanceInvoice;
  contract?: FinanceContract;
  onClose: () => void;
}) {
  const store = useStore();
  const today = civilDate(new Date().toISOString());
  const balance = invoiceBalanceCents(invoice, store.receipts ?? []);
  const [amount, setAmount] = useState(formatCents(balance));
  const [receivedOn, setReceivedOn] = useState(today);
  const [time, setTime] = useState(localParts(new Date()).time);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const closesSale = !contract?.closedOn;

  const submit = async () => {
    if (saving) return;
    const cents = parseCents(amount);
    const next: Record<string, string> = {};
    if (!cents) next.amount = "Informe um valor maior que zero.";
    else if (cents > balance) next.amount = `O saldo desta parcela é ${formatCents(balance)}.`;
    if (receivedOn > today) next.receivedOn = "A data do recebimento não pode ser futura.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    const result = await store.registerReceipt({
      invoiceId: invoice.id,
      amountCents: cents as number,
      receivedOn,
      receivedAtTime: time,
    });
    setSaving(false);
    if (!result.ok) {
      setErrors({ amount: result.error.message });
      return;
    }
    showFeedback(
      result.value.lead?.clientSince
        ? "Recebimento registrado. A venda foi fechada e o lead virou cliente."
        : "Recebimento registrado.",
      "success",
    );
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={amount !== formatCents(balance) || receivedOn !== today}
      title="Registrar recebimento"
      description={
        closesSale
          ? "Este é o primeiro recebimento do contrato: ele fecha a venda e converte o lead em cliente, na mesma operação."
          : "O recebimento abate o saldo da parcela e entra na jornada."
      }
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="primary" onClick={submit} loading={saving}>
            Registrar
          </Button>
        </>
      }
    >
      <p className="cx-note">
        {invoice.description} · vencimento {invoice.dueOn.split("-").reverse().join("/")} · saldo{" "}
        {formatCents(balance)}
      </p>
      <div className="cx-form-grid">
        <Field label="Valor recebido" required error={errors.amount}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          )}
        </Field>
        <Field label="Data" required error={errors.receivedOn}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="date"
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={receivedOn}
              onChange={(event) => setReceivedOn(event.target.value)}
            />
          )}
        </Field>
        <Field label="Hora" required>
          {({ id }) => (
            <TextInput id={id} type="time" value={time} onChange={(event) => setTime(event.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/** Data civil em pt-BR, no fuso do produto (§7.6). */
const dayLabel = (iso: string) => civilDate(iso).split("-").reverse().join("/");

/* --- Reunião -------------------------------------------------------------------- */

export function MeetingDialog({
  leadId,
  membershipId,
  meeting,
  onClose,
}: {
  leadId: string;
  membershipId?: string;
  /** Reunião existente: o diálogo vira edição, como no protótipo. */
  meeting?: Meeting;
  onClose: () => void;
}) {
  const store = useStore();
  const defaults = localParts(
    meeting ? new Date(meeting.startsAt) : new Date(Date.now() + 24 * 60 * 60 * 1000),
  );
  const [title, setTitle] = useState(meeting?.title ?? "");
  const [date, setDate] = useState(defaults.date);
  const [time, setTime] = useState(meeting ? defaults.time : "10:00");
  const [duration, setDuration] = useState(String(meeting?.durationMinutes ?? 60));
  const [participants, setParticipants] = useState((meeting?.participants ?? []).join(", "));
  const [link, setLink] = useState(meeting?.link ?? "");
  const [notes, setNotes] = useState(meeting?.notes ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (saving) return;
    const startsAt = toInstant(date, time);
    const next: Record<string, string> = {};
    if (!title.trim()) next.title = "Informe o objetivo da reunião.";
    if (!startsAt) next.date = "Informe uma data e uma hora válidas.";
    setErrors(next);
    if (Object.keys(next).length) return;

    const pessoas = participants
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    setSaving(true);
    if (meeting) {
      // Editar são dois comandos: o horário é remarcação, o resto é conteúdo.
      // Só o que mudou é enviado — remarcar uma reunião para o mesmo horário
      // registraria uma decisão que ninguém tomou.
      const remarcou =
        startsAt !== meeting.startsAt || Number(duration) !== meeting.durationMinutes;
      const passo = remarcou
        ? await store.rescheduleMeeting({
            meetingId: meeting.id,
            startsAt: startsAt as string,
            durationMinutes: Number(duration) || 60,
          })
        : { ok: true as const };
      const conteudo = passo.ok
        ? await store.updateMeeting({
            meetingId: meeting.id,
            title: title.trim(),
            participants: pessoas,
            notes: notes.trim() || undefined,
            link: link.trim() || undefined,
          })
        : passo;
      setSaving(false);
      if (!passo.ok) {
        setErrors({ date: passo.error.message });
        return;
      }
      if (!conteudo.ok) {
        setErrors({ title: conteudo.error.message });
        return;
      }
      showFeedback("Reunião atualizada.", "success");
      onClose();
      return;
    }

    const result = await store.scheduleMeeting({
      leadId,
      membershipId,
      title: title.trim(),
      startsAt: startsAt as string,
      durationMinutes: Number(duration) || 60,
      participants: pessoas,
      link: link.trim() || undefined,
      notes: notes.trim() || undefined,
    });
    setSaving(false);
    if (!result.ok) {
      setErrors({ title: result.error.message });
      return;
    }
    showFeedback("Reunião agendada.", "success");
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={Boolean(title.trim() || participants.trim() || notes.trim())}
      title={meeting ? "Editar reunião" : "Agendar reunião"}
      description="Agendar não conta como interação; confirmar que ela aconteceu é que conta."
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="primary" onClick={submit} loading={saving}>
            Agendar
          </Button>
        </>
      }
    >
      <div className="cx-form-grid">
        <Field label="Objetivo" required error={errors.title} className="cx-span-2">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={title}
              maxLength={160}
              placeholder="Ex.: apresentação da proposta"
              onChange={(event) => setTitle(event.target.value)}
            />
          )}
        </Field>
        <Field label="Data" required error={errors.date}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="date"
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          )}
        </Field>
        <Field label="Hora" required>
          {({ id }) => (
            <TextInput id={id} type="time" value={time} onChange={(event) => setTime(event.target.value)} />
          )}
        </Field>
        <Field label="Duração" required>
          {({ id }) => (
            <Select
              id={id}
              size="field"
              label="Duração da reunião"
              value={duration}
              options={[
                { value: "30", label: "30 minutos" },
                { value: "60", label: "1 hora" },
                { value: "90", label: "1 hora e 30" },
                { value: "120", label: "2 horas" },
              ]}
              onChange={setDuration}
            />
          )}
        </Field>
        <Field label="Participantes" help="Nomes ou e-mails separados por vírgula.">
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              value={participants}
              onChange={(event) => setParticipants(event.target.value)}
            />
          )}
        </Field>
        <Field label="Link da sala" className="cx-span-2">
          {({ id }) => (
            <TextInput
              id={id}
              value={link}
              placeholder="https://"
              onChange={(event) => setLink(event.target.value)}
            />
          )}
        </Field>
        <Field label="Pauta" className="cx-span-2">
          {({ id }) => (
            <TextArea
              id={id}
              rows={3}
              value={notes}
              maxLength={2000}
              onChange={(event) => setNotes(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/* --- Confirmação simples --------------------------------------------------------- */

export function ConfirmDialog({
  title,
  question,
  confirmLabel,
  danger = false,
  nominal,
  onConfirm,
  onClose,
}: {
  title: string;
  question: string;
  confirmLabel: string;
  danger?: boolean;
  /**
   * Nome que precisa ser digitado para liberar a ação.
   *
   * É a confirmação nominal do §18.1: irreversível sem volta pede mais do que
   * um clique a mais, pede o nome do alvo.
   */
  nominal?: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  const liberado = !nominal || typed.trim() === nominal.trim();

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={false}
      title={title}
      description={question}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button
            variant={danger ? "danger" : "primary"}
            disabled={!liberado}
            title={liberado ? undefined : `Digite “${nominal}” para liberar.`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {/* Sem confirmação nominal a pergunta já é a descrição do diálogo;
          repeti-la aqui faria o leitor de tela anunciar o mesmo texto duas
          vezes. */}
      {nominal ? (
        <Field label={`Digite “${nominal}” para confirmar`} required>
          {({ id }) => (
            <TextInput
              id={id}
              value={typed}
              autoComplete="off"
              onChange={(event) => setTyped(event.target.value)}
            />
          )}
        </Field>
      ) : null}
    </Dialog>
  );
}

/**
 * Cancelar uma reunião.
 *
 * O motivo é obrigatório no comando, então não dá para usar a confirmação
 * simples: cancelar sem dizer por quê deixaria a agenda com um buraco que
 * ninguém consegue explicar depois. Cancelar não conta como interação (§7.7).
 */
export function CancelMeetingDialog({
  meeting,
  onClose,
}: {
  meeting: Meeting;
  onClose: () => void;
}) {
  const store = useStore();
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (saving) return;
    if (!reason.trim()) {
      setError("Informe o motivo do cancelamento.");
      return;
    }
    setSaving(true);
    const result = await store.cancelMeeting({ meetingId: meeting.id, reason: reason.trim() });
    setSaving(false);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    showFeedback("Reunião cancelada.", "success");
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={Boolean(reason.trim())}
      title="Cancelar reunião"
      description={`${meeting.title} · ${dayLabel(meeting.startsAt)}`}
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Manter agendada
          </Button>
          <ToolbarSpacer />
          <Button variant="danger" onClick={submit} loading={saving}>
            Cancelar reunião
          </Button>
        </>
      }
    >
      <Field label="Motivo" required error={error}>
        {({ id, describedBy, invalid }) => (
          <TextArea
            id={id}
            rows={3}
            value={reason}
            maxLength={300}
            placeholder="Ex.: o cliente pediu para remarcar depois do fechamento do mês."
            aria-describedby={describedBy}
            aria-invalid={invalid}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>
    </Dialog>
  );
}

/**
 * Cobrança extra de um contrato.
 *
 * Extra é fatura avulsa pendurada no contrato: entra na receita, não vira
 * parcela da série e não muda a recorrência (§27.11). Vem do protótipo, que a
 * chama de "Adicionar extra".
 */
export function ExtraInvoiceDialog({
  contract,
  onClose,
}: {
  contract: FinanceContract;
  onClose: () => void;
}) {
  const store = useStore();
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [dueOn, setDueOn] = useState(civilDate(new Date().toISOString()));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (saving) return;
    const cents = parseCents(amount);
    const next: Record<string, string> = {};
    if (!description.trim()) next.description = "Descreva a cobrança.";
    if (!cents) next.amount = "Informe um valor maior que zero.";
    if (dueOn < contract.startOn) next.dueOn = "O vencimento não pode ser antes do início do contrato.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    const result = await store.addExtraInvoice({
      contractId: contract.id,
      description: description.trim(),
      amountCents: cents as number,
      dueOn,
    });
    setSaving(false);
    if (!result.ok) {
      setErrors({ description: result.error.message });
      return;
    }
    showFeedback("Cobrança extra adicionada.", "success");
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={Boolean(description.trim() || amount.trim())}
      title="Adicionar extra"
      description={`${contract.description} · o extra entra na receita, fora da série mensal.`}
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="primary" onClick={submit} loading={saving}>
            Adicionar extra
          </Button>
        </>
      }
    >
      <div className="cx-form-grid">
        <Field label="Descrição" required error={errors.description} className="cx-span-2">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={description}
              maxLength={200}
              placeholder="Ex.: produção de criativos"
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>
        <Field label="Valor" required error={errors.amount}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={amount}
              placeholder="R$ 0,00"
              onChange={(event) => setAmount(event.target.value)}
            />
          )}
        </Field>
        <Field label="Vencimento" required error={errors.dueOn}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              type="date"
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={dueOn}
              onChange={(event) => setDueOn(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * Encerramento do contrato.
 *
 * Parcelas com vencimento posterior à data saem da previsão; recebimentos e
 * dívidas anteriores permanecem, porque encerrar não reescreve o passado.
 */
export function CancelContractDialog({
  contract,
  onClose,
}: {
  contract: FinanceContract;
  onClose: () => void;
}) {
  const store = useStore();
  const [cancelledOn, setCancelledOn] = useState(civilDate(new Date().toISOString()));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (saving) return;
    if (cancelledOn < contract.startOn) {
      setError("O encerramento não pode ser antes do início do contrato.");
      return;
    }
    setSaving(true);
    const result = await store.cancelContract({ contractId: contract.id, cancelledOn });
    setSaving(false);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    showFeedback("Contrato encerrado.", "success");
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Encerrar contrato"
      description={`${contract.description} · parcelas posteriores ao encerramento saem da previsão.`}
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Manter contrato
          </Button>
          <ToolbarSpacer />
          <Button variant="danger" onClick={submit} loading={saving}>
            Encerrar contrato
          </Button>
        </>
      }
    >
      <p className="cx-dialog-note">
        Recebimentos já registrados e parcelas vencidas em aberto permanecem: encerrar interrompe a
        recorrência, não apaga o histórico.
      </p>
      <Field label="Data de encerramento" required error={error}>
        {({ id, describedBy, invalid }) => (
          <TextInput
            id={id}
            type="date"
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            value={cancelledOn}
            onChange={(event) => setCancelledOn(event.target.value)}
          />
        )}
      </Field>
    </Dialog>
  );
}
